// ms-service.js
import { normalizeFeatureResponse } from './normalize';

const EMPTY_FLAGS = Object.freeze({});
const EMPTY_RAW = Object.freeze({});
const stores = new Map();

const FAKE_GENERAL_ENDPOINT = '/cashmarket/api/v3/public/settings/GENERAL';

const FAKE_PAGE_ENDPOINT = '';

const USE_MOCK_GENERAL = false;

const MOCK_GENERAL_RESPONSE = {
    code: '0',
    message: 'SUCCESS',
    timestamp: '2026-01-14T07:16:45.182923740Z',
    data: {
        VIP_SYSTEM: {
            main_setting: { enable: true },
        },
        LUCKY_WHEEL: {
            main_setting: { enable: true },
        },
        INSTANT_REBATE: {
            main_setting: { enable: false },
        },
        SEAMLESS_WALLET: {
            main_setting: { enable: true },
        },
    },
};

function getStore(scope) {
    if (!stores.has(scope)) {
        stores.set(scope, {
            updatedAt: 0,
            flags: null,
            raw: null,
            inflight: null,
            listeners: new Set(),
        });
    }
    return stores.get(scope);
}

function notify(scope) {
    getStore(scope).listeners.forEach((fn) => fn());
}

// you’ll replace these with real endpoints
async function fetchGeneral(currencyLang) {
    if (USE_MOCK_GENERAL) {
        return Promise.resolve(MOCK_GENERAL_RESPONSE.data);
    }

    if (!currencyLang) return;

    try {
        const res = await fetch(`${FAKE_GENERAL_ENDPOINT}?currencyCode=${currencyLang}`);

        if (!res.ok) {
            throw new Error(`general config failed: ${res.status}`);
        }

        const json = await res.json();

        return json?.data ?? {};
    } catch (err) {
        console.error('fetchGeneral error:', err);
        throw err;
    }

    // return FAKE_GENERAL_ENDPOINT;
}

async function fetchPage(scope) {
    // scope example: "page:wallet" -> "/api/page-feature-config/wallet"
    const pageName = scope.split(':')[1];
    const res = await fetch(FAKE_PAGE_ENDPOINT || `/api/page-feature-config/${pageName}`);
    if (!res.ok) throw new Error(`page config failed (${scope}): ${res.status}`);
    return res.json();
}

export function updateFakeGeneralData(updater) {
    const store = getStore('global');

    // Ensure store is initialized
    if (!store.raw || !store.flags) {
        throw new Error('featureConfigClient not initialized yet');
    }

    Object.keys(updater).forEach((key) => {
        store.raw[key] = {
            ...store.raw[key],
            ...updater[key],
        };
    });

    // 2️⃣ Recompute flags from raw
    Object.keys(updater).forEach((key) => {
        const enabled = store.raw[key]?.main_feature?.enable;
        store.flags[key] = !!enabled;
    });

    notify('global');
}

export const featureConfigClient = {
    async init(scope = 'global', opts = {}, currencyLang = localStorage.getItem('currencyLang')) {
        const store = getStore(scope);
        if (store.flags && !opts.force) return store;

        if (!store.inflight || opts.force) {
            const fetcher = opts.fetcher || (scope === 'global' ? () => fetchGeneral(currencyLang) : () => fetchPage(scope));

            store.inflight = Promise.resolve()
                .then(fetcher)
                .then((data) => {
                    const normalized = normalizeFeatureResponse(data);

                    // store normalized result
                    store.flags = normalized.flags || EMPTY_FLAGS;
                    store.raw = normalized.raw || EMPTY_RAW;
                    store.updatedAt = Date.now();

                    notify(scope);
                    return store;
                })
                .finally(() => {
                    store.inflight = null;
                });
        }

        return store.inflight;
    },

    async refresh(scope = 'global', opts = {}) {
        return this.init(scope, { ...opts, force: true });
    },

    // boolean read
    getFlag(scope = 'global', key, fallback = false) {
        const store = getStore(scope);
        const flags = store.flags || EMPTY_FLAGS;
        return key in flags ? !!flags[key] : fallback;
    },

    // raw read (full object)
    getRaw(scope = 'global', key) {
        const store = getStore(scope);
        const raw = store.raw || EMPTY_RAW;
        return raw[key];
    },

    // snapshot for react (must be stable)
    getAllFlags(scope = 'global') {
        const store = getStore(scope);
        return store.flags || EMPTY_FLAGS;
    },

    subscribe(scope = 'global', fn) {
        const store = getStore(scope);
        store.listeners.add(fn);
        return () => store.listeners.delete(fn);
    },

    // optional: merge global + page so page reads "effective" config
    // page api “also returns general content”, but don’t trust that forever.
    getEffectiveFlag(scope = 'global', key, fallback = false) {
        if (scope === 'global') return this.getFlag('global', key, fallback);
        // page overrides global if present
        const pageVal = this.getFlag(scope, key, undefined);
        if (pageVal !== undefined) return pageVal;
        return this.getFlag('global', key, fallback);
    },

    getEffectiveRaw(scope = 'global', key) {
        if (scope === 'global') return this.getRaw('global', key);
        return this.getRaw(scope, key) ?? this.getRaw('global', key);
    },
};
